/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2023 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

import ResizeObserver from 'resize-observer-polyfill';
import * as React from 'react';
import doLayout, { Position } from './layout';

const INITIAL_HEIGHT = 500;

const DEFAULT_COLUMNS = 4;
const DEFAULT_COLUMN_WIDTH = 200;
const DEFAULT_GUTTER = 10;

export interface BreakPoint {
  minScreenWidth: number;
  maxScreenWidth: number;
  columnWidth: number;
  columns: number;
}

export interface GridProps {
  columnWidth: number;
  columns: number;
  gutterWidth: number;
  gutterHeight: number;
  responsive?: boolean | ResponsiveConfigObject;
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactChildren;
}

export interface ResponsiveConfigObject {
  minPadding?: number;
  maxWidth?: number;
  customBreakPoints?: BreakPoint[];
}

interface GridState {
  positions: Position[];
  wrappedItems: any[];
  gridWidth: number;
  gridHeight: number;
}

export default class Grid extends React.PureComponent<GridProps, GridState> {

  static defaultProps = {
    columns: DEFAULT_COLUMNS,
    columnWidth: DEFAULT_COLUMN_WIDTH,
    gutterWidth: DEFAULT_GUTTER,
    gutterHeight: DEFAULT_GUTTER,
  }

  wrappedRefs: any[] = [];

  state = {
    positions: [],
    wrappedItems: [],
    gridWidth: 0,
    gridHeight: 0,
  }

  private resizeObserver = new ResizeObserver(() => {
    this.doLayout();
  });

  public ref: any = React.createRef()

  componentDidMount() {
    const { children } = this.props;
    const wrappedItems = this.createWrappedItems((children as any)!);
    this.doLayout(wrappedItems);
    setTimeout(this.observeChildren);
  }

  componentDidUpdate(prevProps: GridProps) {
    const { columnWidth, gutterWidth, gutterHeight, columns, children } = this.props;
    if (children !== prevProps.children) {
      const wrappedItems = this.createWrappedItems((children as any)!);
      this.doLayout(wrappedItems);
      setTimeout(this.observeChildren);
    }
    if (columnWidth !== prevProps.columnWidth ||
      gutterHeight !== prevProps.gutterHeight ||
      gutterWidth !== prevProps.gutterWidth ||
      columns !== prevProps.columns
    ) {
      this.doLayout();
    }
  }

  componentWillUnmount(): void {
    this.unobserveChildren();
  }

  observeChildren = () => {
    const container: any = this.ref.current;
    let len = 0;
    if (container && container.children && container.children.length) {
      len = container.children.length;
    }
    for (let i = 0; i < len; i++) {
      this.resizeObserver.observe(container.children[i]);
    }
  }

  unobserveChildren = () => {
    const container: any = this.ref.current;;
    let len = 0;
    if (container && container.children && container.children.length) {
      len = container.children.length;
    }
    for (let i = 0; i < len; i++) {
      this.resizeObserver.unobserve(container.children[i]);
    }
    this.resizeObserver.disconnect();
  }



  createWrappedItems = (children: React.ReactNode): any[] => {
    this.wrappedRefs = [];
    const items = React.Children.toArray(children);
    if (!Array.isArray(items)) {
      return [];
    }
    const wrappedItems = items.map((item: any, index: number) => {
      return (
        <div key={item.key || `grid-${index}`}>
          {item}
        </div>
      )
    });
    return wrappedItems;
  }


  doLayout = (wrappedItems?: any[]) => {
    const { columnWidth, gutterWidth, gutterHeight, columns } = this.props;
    const container: any = this.ref.current;
    let nextWrappedItems: any[] = this.state.wrappedItems;
    if (Array.isArray(wrappedItems)) {
      nextWrappedItems = wrappedItems;
    }
    const itemHeights = nextWrappedItems.map((_item, index) => {
      if (container && container.children && container.children[index]) {
        return container.children[index].clientHeight;
      }
      return INITIAL_HEIGHT;
    })

    this.setState({
      wrappedItems: nextWrappedItems,
      ...doLayout(
        itemHeights,
        { columnWidth, gutterWidth, gutterHeight, columns }
      )
    });
  }

  render() {
    const { gridHeight, positions, wrappedItems } = this.state;
    const { columnWidth, style, className } = this.props;
    return (
      <div
        style={{
          ...style,
          position: 'relative',
          width: "100%",
          height: gridHeight,
          boxSizing: 'content-box',
        }}
        className={className}
        ref={this.ref}
      >
        {
          wrappedItems.map((item, index) => {
            return React.cloneElement(item, {
              style: {
                position: 'absolute',
                left: positions[index][0],
                top: positions[index][1],
                width: columnWidth,
                boxSizing: 'border-box',
              }
            })
          })
        }
      </div>
    );

  }
}

